import React, { useState, useRef, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatFileSize, cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, X, Plus, Folder, FolderOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useRole } from "@/hooks/useRole";

interface DocumentUploadProps {
  onUploadSuccess?: (document: any) => void;
  ownerType: "agency" | "client" | "employee" | "prospect";
  agencyId?: number;
  clientId?: number;
  employeeId?: number;
  prospectId?: number;
  folderId?: number | null;
  // when provided, shows a destination-folder picker; folderId is the default
  folders?: { id: number; name: string }[];
  defaultCategory?: string;
  fixedCategory?: boolean;
  tags?: string[];
  trigger?: React.ReactNode;
  categoryOptions?: { value: string; label: string }[];
  // Optional: show a compliance type selector and include it in upload metadata
  complianceTypeOptions?: { value: string; label: string }[];
}

// Default category options used as fallback when no categoryOptions prop
// is passed and no dynamic org categories are available
const builtInCategoryOptions = [
  { value: "policy", label: "Policy" },
  { value: "compliance", label: "Compliance" },
  { value: "form", label: "Form" },
  { value: "license", label: "License" },
  { value: "insurance", label: "Insurance" },
  { value: "other", label: "Other" },
];

export function DocumentUpload({
  onUploadSuccess,
  ownerType,
  agencyId,
  clientId,
  employeeId,
  prospectId,
  folderId,
  folders,
  defaultCategory,
  fixedCategory,
  tags,
  trigger,
  categoryOptions,
  complianceTypeOptions,
}: DocumentUploadProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState<string>(defaultCategory || "");
  const [description, setDescription] = useState<string>("");
  const [shareWithEmployer, setShareWithEmployer] = useState(false);
  const [shareWithBroker, setShareWithBroker] = useState(false);
  const [visibleToEmployee, setVisibleToEmployee] = useState(false);
  // Hide whichever checkbox matches your own role — sharing with yourself is a no-op.
  const { currentRole } = useRole();
  const showVisibilityControls = ownerType === "employee";
  const showEmployerCheckbox = showVisibilityControls && currentRole !== "employer";
  const showBrokerCheckbox = showVisibilityControls && currentRole !== "broker";
  const showEmployeeCheckbox = showVisibilityControls && currentRole !== "employee";
  const [complianceType, setComplianceType] = useState<string>("");
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  // Employee visibility (client documents): default on = inherit folder
  // visibility, matching pre-existing behavior.
  const [visibleToEmployees, setVisibleToEmployees] = useState(true);
  const [visibilityStartDate, setVisibilityStartDate] = useState<string>("");
  const [visibilityEndDate, setVisibilityEndDate] = useState<string>("");
  const NO_FOLDER = "root";
  const [selectedFolderId, setSelectedFolderId] = useState<string>(NO_FOLDER);
  useEffect(() => {
    if (isOpen) setSelectedFolderId(folderId ? String(folderId) : NO_FOLDER);
  }, [isOpen, folderId]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Resolve agencyId from current user when not explicitly provided
  const { data: currentUser } = useQuery<any>({
    queryKey: ["/api/auth/user"],
    enabled: ownerType === "agency" && !agencyId,
  });
  const resolvedAgencyId = agencyId || currentUser?.id;

  // Determine owner context for fetching categories
  const orgOwnerType = clientId ? "client" : prospectId ? "prospect" : "agency";
  const orgOwnerId = orgOwnerType === "client" ? clientId : orgOwnerType === "prospect" ? prospectId : resolvedAgencyId;

  // Fetch dynamic document categories
  const { data: orgCategories = [] } = useQuery({
    queryKey: ["/api/org/document-categories", orgOwnerType, orgOwnerId],
    enabled: !!orgOwnerId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/document-categories?ownerType=${orgOwnerType}&ownerId=${orgOwnerId}`
      );
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json();
    },
  });

  const addCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      const ownerPayload = orgOwnerType === "agency"
        ? { ownerType: "agency" as const, agencyId: String(orgOwnerId), clientId: null, prospectId: null }
        : orgOwnerType === "prospect"
        ? { ownerType: "prospect" as const, agencyId: null, clientId: null, prospectId: Number(orgOwnerId) }
        : { ownerType: "client" as const, agencyId: null, clientId: Number(orgOwnerId), prospectId: null };

      return apiRequest("POST", "/api/org/document-categories", {
        ...ownerPayload,
        name,
        description: null,
      });
    },
    onSuccess: async (res) => {
      const created = await res.json();
      queryClient.invalidateQueries({
        queryKey: ["/api/org/document-categories", orgOwnerType, orgOwnerId],
      });
      setCategory(String(created?.id ?? ""));
      setIsAddCategoryOpen(false);
      setNewCategoryName("");
      toast({ title: "Category added", variant: "success" });
    },
    onError: () =>
      toast({ title: "Failed to add category", variant: "destructive" }),
  });

  const maxFileSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/jpeg",
    "image/png",
    "image/gif",
    "text/plain",
    "text/csv",
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description:
          "Please select a valid document type (PDF, Word, Excel, Image, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      toast({
        title: "File too large",
        description: "Please select a file smaller than 10MB",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !category) {
      toast({
        title: "Missing information",
        description: "Please select a file and category",
        variant: "destructive",
      });
      return;
    }

    if (
      Array.isArray(complianceTypeOptions) &&
      complianceTypeOptions.length > 0 &&
      !complianceType
    ) {
      toast({
        title: "Missing information",
        description: "Please select a compliance type",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      // Find the category name from the selected category value
      const selectedCategoryObj = options.find(opt => opt.value === category);
      const categoryName = selectedCategoryObj?.label || category || "other";

      const formData = new FormData();
      formData.append("document", selectedFile);
      formData.append("category", categoryName); // Send category name as text
      // Only send categoryId when the selection is a dynamic org category —
      // built-in defaults (policy, form, …) have no id.
      if (!categoryOptions && dynamicOptions.some((o) => o.value === category)) {
        formData.append("categoryId", category);
      }
      if (description) formData.append("description", description);
      const combinedTags = [
        ...(Array.isArray(tags) ? tags : []),
        ...(complianceType ? [`complianceType:${complianceType}`] : []),
      ];
      if (combinedTags.length > 0) {
        formData.append("tags", JSON.stringify(combinedTags));
      }
      if (complianceType) {
        formData.append("complianceType", complianceType);
      }
      formData.append("sharedWithEmployer", String(shareWithEmployer));
      formData.append("sharedWithBroker", String(shareWithBroker));
      formData.append("visibleToEmployee", String(visibleToEmployee));
      const effectiveFolderId =
        Array.isArray(folders) && folders.length > 0
          ? selectedFolderId !== NO_FOLDER
            ? parseInt(selectedFolderId, 10)
            : null
          : folderId ?? null;
      if (effectiveFolderId !== null) {
        formData.append("folderId", String(effectiveFolderId));
      }
      if (ownerType === "client") {
        formData.append("visibleToEmployees", String(visibleToEmployees));
        if (visibleToEmployees && visibilityStartDate)
          formData.append("visibilityStartDate", visibilityStartDate);
        if (visibleToEmployees && visibilityEndDate)
          formData.append("visibilityEndDate", visibilityEndDate);
      }

      // Choose endpoint based on ownerType
      let endpoint: string;
      if (ownerType === "client" && clientId) {
        endpoint = `/api/clients/${clientId}/documents/upload`;
      } else if (ownerType === "employee" && clientId && employeeId) {
        endpoint = `/api/clients/${clientId}/employees/${employeeId}/documents/upload`;
      } else if (ownerType === "employee" && employeeId) {
        endpoint = `/api/employees/${employeeId}/documents/upload`;
      } else if (ownerType === "prospect" && prospectId) {
        endpoint = `/api/prospects/${prospectId}/documents/upload`;
      } else {
        endpoint = "/api/documents/upload";
      }

      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const rawText = await response.text();
      const parsed = (() => {
        if (!rawText) return null;
        try {
          return JSON.parse(rawText);
        } catch {
          return null;
        }
      })();

      if (!response.ok) {
        const messageFromJson = (parsed as any)?.message;
        const messageFromText = rawText && !parsed ? rawText : undefined;
        throw new Error(messageFromJson || messageFromText || "Upload failed");
      }

      const result = (parsed as any) ?? {};

      toast({
        title: "Upload successful",
        description: "Document has been uploaded successfully",
      });

      // Reset form
      setSelectedFile(null);
      setCategory(defaultCategory || "");
      setDescription("");
      setVisibleToEmployees(true);
      setVisibilityStartDate("");
      setVisibilityEndDate("");
      setComplianceType("");
      setShareWithEmployer(false);
      setShareWithBroker(false);
      setVisibleToEmployee(false);
      setIsOpen(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onUploadSuccess) {
        onUploadSuccess(result.document);
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload document",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  // Convert org categories to options format
  const dynamicOptions = useMemo(() => {
    return (orgCategories as any[]).map((cat: any) => ({
      value: String(cat.id),
      label: cat.name,
    }));
  }, [orgCategories]);

  // Built-in defaults always remain available; custom org categories are
  // appended (a custom category whose name shadows a built-in wins, so its
  // categoryId is preserved on upload).
  const options = useMemo(() => {
    if (Array.isArray(categoryOptions) && categoryOptions.length > 0) {
      return categoryOptions;
    }
    const dynamicLabels = new Set(
      dynamicOptions.map((o) => o.label.trim().toLowerCase())
    );
    return [
      ...builtInCategoryOptions.filter(
        (o) => !dynamicLabels.has(o.label.trim().toLowerCase())
      ),
      ...dynamicOptions,
    ];
  }, [categoryOptions, dynamicOptions]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Select a file, category, and optional details, then upload your document.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Selection */}
          <div>
            <Label>Select File</Label>
            <div className="mt-2">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv"
              />

              {!selectedFile ? (
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full h-20 border-dashed"
                >
                  <div className="text-center">
                    <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Click to select file
                    </span>
                  </div>
                </Button>
              ) : (
                <div className="border rounded-lg p-3 bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(selectedFile.size)}
                      </p>
                    </div>
                    <Button
                      onClick={removeFile}
                      variant="ghost"
                      size="sm"
                      className="ml-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Supported: PDF, Word, Excel, Images, Text files. Max size: 10MB
            </p>
          </div>

          {/* Category */}
          <div>
            <Label htmlFor="category">Category *</Label>
            <div className="flex gap-2">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger disabled={!!fixedCategory} className="flex-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!fixedCategory && !categoryOptions && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setIsAddCategoryOpen(true)}
                  title="Add new category"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Add Category Dialog */}
            {isAddCategoryOpen && (
              <Dialog
                open={isAddCategoryOpen}
                onOpenChange={setIsAddCategoryOpen}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Document Category</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="newCategoryName">Category Name</Label>
                      <Input
                        id="newCategoryName"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Enter category name"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsAddCategoryOpen(false);
                          setNewCategoryName("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (newCategoryName.trim()) {
                            addCategoryMutation.mutate(newCategoryName.trim());
                          }
                        }}
                        disabled={!newCategoryName.trim()}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {Array.isArray(folders) && folders.length > 0 && (
            <div>
              <Label>Folder</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto pr-1">
                {[{ id: null as number | null, name: "No folder" }, ...folders].map(
                  (f) => {
                    const value = f.id === null ? NO_FOLDER : String(f.id);
                    const isSelected = selectedFolderId === value;
                    const FolderIcon = isSelected ? FolderOpen : Folder;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setSelectedFolderId(value)}
                        className={cn(
                          "flex items-start gap-2 rounded-lg border p-2.5 text-left text-sm transition-colors hover:bg-muted/40",
                          isSelected
                            ? "border-primary ring-1 ring-primary bg-muted/40"
                            : "border-border"
                        )}
                      >
                        <FolderIcon
                          className={cn(
                            "h-4 w-4 shrink-0 mt-0.5",
                            f.id === null
                              ? "text-muted-foreground"
                              : "text-amber-500"
                          )}
                        />
                        <span className="min-w-0 break-words">{f.name}</span>
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          )}

          {/* Compliance Type (optional) */}
          {Array.isArray(complianceTypeOptions) &&
            complianceTypeOptions.length > 0 && (
              <div>
                <Label htmlFor="complianceType">Compliance Type *</Label>
                <Select
                  value={complianceType}
                  onValueChange={setComplianceType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select compliance type" />
                  </SelectTrigger>
                  <SelectContent>
                    {complianceTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the document"
              rows={3}
            />
          </div>

          {/* Employee visibility (client documents only). The folder's own
              employee-visibility flag still gates everything — this narrows
              per document, with an optional visibility window. */}
          {ownerType === "client" && (
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="visible-to-employees" className="cursor-pointer">
                    Visible to employees
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Shown in the employee portal when its folder is shared with
                    employees.
                  </p>
                </div>
                <input
                  id="visible-to-employees"
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={visibleToEmployees}
                  onChange={(e) => setVisibleToEmployees(e.target.checked)}
                />
              </div>
              {visibleToEmployees && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="visibility-start" className="text-xs">
                      Visible from (optional)
                    </Label>
                    <Input
                      id="visibility-start"
                      type="date"
                      value={visibilityStartDate}
                      onChange={(e) => setVisibilityStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="visibility-end" className="text-xs">
                      Visible until (optional)
                    </Label>
                    <Input
                      id="visibility-end"
                      type="date"
                      value={visibilityEndDate}
                      onChange={(e) => setVisibilityEndDate(e.target.value)}
                    />
                  </div>
                  <p className="col-span-2 text-xs text-muted-foreground">
                    Outside the window the document stays in this folder but
                    disappears from the employee portal.
                  </p>
                </div>
              )}
            </div>
          )}

          {showVisibilityControls && (
            <div className="space-y-2">
              <Label>Visible to</Label>
              {showEmployerCheckbox && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="shareWithEmployer"
                    checked={shareWithEmployer}
                    onCheckedChange={(checked) => setShareWithEmployer(checked === true)}
                  />
                  <Label htmlFor="shareWithEmployer" className="font-normal cursor-pointer">
                    Employer
                  </Label>
                </div>
              )}
              {showBrokerCheckbox && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="shareWithBroker"
                    checked={shareWithBroker}
                    onCheckedChange={(checked) => setShareWithBroker(checked === true)}
                  />
                  <Label htmlFor="shareWithBroker" className="font-normal cursor-pointer">
                    Broker
                  </Label>
                </div>
              )}
              {showEmployeeCheckbox && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="visibleToEmployee"
                    checked={visibleToEmployee}
                    onCheckedChange={(checked) => setVisibleToEmployee(checked === true)}
                  />
                  <Label htmlFor="visibleToEmployee" className="font-normal cursor-pointer">
                    Employee
                  </Label>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !category || uploading}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
